import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import authService from '../../services/authService.js';
import { RefreshCw, Filter } from 'lucide-react';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const filteredLogs = useMemo(() => {
    if (!search) return logs;
    const q = search.toLowerCase();
    return logs.filter(l =>
      (l.username || '').toLowerCase().includes(q) ||
      (l.action || '').toLowerCase().includes(q) ||
      (l.endpoint || '').toLowerCase().includes(q) ||
      (l.ip_address || '').toLowerCase().includes(q) ||
      (l.user_agent || '').toLowerCase().includes(q)
    );
  }, [logs, search]);

  const fetchUsers = async () => {
    try {
      const list = await authService.getAllUsers();
      setUsers(list);
    } catch (e) {
      // non-fatal
      console.error('Failed to load users', e);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: limit + 1, offset };
      if (selectedUserId !== 'all') params.user_id = selectedUserId;
      const data = await authService.getActivityLogs(params);
      const arr = data?.logs || [];
      setHasMore(arr.length > limit);
      setLogs(arr.slice(0, limit));
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to fetch activity logs');
      setLogs([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, limit, offset]);

  const onRefresh = () => {
    setOffset(0);
    fetchLogs();
  };

  const formatDateTime = (ts) => (ts ? new Date(ts).toLocaleString() : '');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h1 mb-0">Activity Logs</h1>
        <div className="d-flex gap-2">
          <Button variant="outline" onClick={onRefresh} disabled={loading}>
            <RefreshCw className="me-2" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <CardHeader className="d-flex align-items-center gap-3">
          <strong><Filter className="me-2" /> Filters</strong>
          <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
            <input
              type="text"
              className="form-control"
              style={{ minWidth: 240 }}
              placeholder="Search (username, action, endpoint, IP, agent)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="form-select"
              value={selectedUserId}
              onChange={(e) => { setOffset(0); setSelectedUserId(e.target.value); }}
            >
              <option value="all">All users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
              ))}
            </select>
            <select
              className="form-select"
              value={limit}
              onChange={(e) => { setOffset(0); setLimit(parseInt(e.target.value, 10)); }}
            >
              {[25,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th style={{whiteSpace:'nowrap'}}>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Endpoint</th>
                  <th>IP</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="text-center py-4">Loading...</td></tr>
                ) : filteredLogs.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-4">No logs found</td></tr>
                ) : (
                  filteredLogs.map(log => (
                    <tr key={log.id}>
                      <td style={{whiteSpace:'nowrap'}}>{formatDateTime(log.timestamp)}</td>
                      <td>{log.username || `#${log.user_id}`}</td>
                      <td>{log.action}</td>
                      <td><code>{log.endpoint}</code></td>
                      <td>{log.ip_address || '-'}</td>
                      <td>{log.request_method || '-'}</td>
                      <td>
                        {typeof log.response_status === 'number' ? (
                          <Badge variant={log.response_status >= 400 ? 'destructive' : 'default'}>
                            {log.response_status}
                          </Badge>
                        ) : '-'}
                      </td>
                      <td style={{maxWidth: 320}}>
                        <span title={log.details || ''} className="text-truncate d-inline-block" style={{maxWidth: 300}}>
                          {log.details || '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">Showing {filteredLogs.length} item(s)</div>
            <div className="btn-group">
              <Button variant="outline" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</Button>
              <Button variant="outline" disabled={!hasMore || loading} onClick={() => setOffset(offset + limit)}>Next</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ActivityLogs;